import WorkflowForm from '../components/workflow-form.component';
import { WorkflowFormData } from '../types/workflow.types';
import { useWorkflowGet, useWorkflowUpdate } from '../hooks/use-workflow.hook';
import { message } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import { extractCreateError } from '../../../../../services/extract-api-error.service';
import { useState } from 'react';
import HeaderRow from '../../../../common/HeaderRow';
import { useTranslation } from 'react-i18next';
import ErrorFallback from '../../../../common/ErrorFallback';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { WithPermissionCheck } from '../../../../auth/WithPermissionCheck';
import PageTitle from '@components/common/PageTitle';

const WorkflowUpdateContainer = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { data, isLoading, error } = useWorkflowGet(id ?? '');
  const { mutateAsync: updateWorkflow, isPending } = useWorkflowUpdate();

  const onFormSubmit = async (formData: WorkflowFormData) => {
    if (!id) return;

    setIsSubmitting(true);

    try {
      // Clean steps - remove undefined iframeContext
      const cleanedSteps = formData.steps.map((step) => {
        const cleanedStep = { ...step };
        if (!cleanedStep.step.iframeContext?.autoDetect) {
          delete cleanedStep.step.iframeContext;
        }
        return cleanedStep;
      });

      await updateWorkflow({
        id,
        data: {
          name: formData.name,
          steps: cleanedSteps,
          mappings: formData.mappings,
          condition: formData.condition,
        },
      });
      message.success(t('crud.update.success', { entity: 'Workflow' }));
      navigate(`/dashboard/workflows/${id}`);
    } catch (error: any) {
      const errorMessage = extractCreateError(
        error,
        t('crud.update.error', { entity: 'Workflow' })
      );
      message.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!id) {
    return (
      <ErrorFallback
        details={t('common.errorLoadingData')}
        icon={<ExclamationCircleOutlined />}
        error={new Error(t('common.invalidIdProvided'))}
        resetErrorBoundary={() => {}}
      />
    );
  }

  if (error) {
    return (
      <>
        <PageTitle title={t('crud.update.title', { entity: 'Workflow' })} />
        <HeaderRow title={t('crud.update.title', { entity: 'Workflow' })} showBackButton={true} />
        <ErrorFallback
          details={t('common.errorLoadingData')}
          icon={<ExclamationCircleOutlined />}
          error={error}
          resetErrorBoundary={() => {}}
        />
      </>
    );
  }

  return (
    <>
      <PageTitle title={t('crud.update.title', { entity: 'Workflow' })} />
      <HeaderRow title={t('crud.update.title', { entity: 'Workflow' })} showBackButton={true} />
      <div className="row mt-3">
        <WorkflowForm
          onSubmit={onFormSubmit}
          isLoading={isLoading || isPending || isSubmitting}
          defaultValues={data?.data}
        />
      </div>
    </>
  );
};

export default WithPermissionCheck(WorkflowUpdateContainer, 'workflow.update');
